import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TENANT } from '../../config';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({
  isOpen,
  onClose,
  onAccept,
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px tolerance
    setHasScrolledToBottom(isAtBottom);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-neutral-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-neutral-700">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-neutral-100">
                Privacy Policy - Bank Account Linking
              </h2>
              <button
                onClick={onClose}
                className="text-neutral-400 hover:text-neutral-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-neutral-400 mt-2">
              Please read and accept our privacy policy to continue with bank account linking.
            </p>
          </div>

          {/* Content */}
          <div
            className="p-6 overflow-y-auto max-h-96"
            onScroll={handleScroll}
          >
            <div className="prose prose-invert max-w-none">
              <h3 className="text-xl font-semibold text-neutral-100 mb-4">
                {TENANT.branding.name} Bank Account Linking Privacy Policy
              </h3>

              <p className="text-neutral-300 mb-4">
                <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>

              <section className="mb-6">
                <h4 className="text-lg font-semibold text-neutral-100 mb-3">
                  1. Information We Collect Through Bank Account Linking
                </h4>
                <p className="text-neutral-300 mb-3">
                  When you link your bank account through our secure banking partner, Stripe, we may collect the following information:
                </p>
                <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
                  <li><strong>Bank Account Information:</strong> Account number (last 4 digits), routing number, account type (checking/savings), and bank name</li>
                  <li><strong>Transaction Data:</strong> Account balances, transaction history, and payment activity (when you authorize access)</li>
                  <li><strong>Verification Data:</strong> Information necessary to verify your identity and account ownership</li>
                  <li><strong>Financial Connections Data:</strong> Data about your connected financial accounts as provided by Stripe</li>
                </ul>
              </section>

              <section className="mb-6">
                <h4 className="text-lg font-semibold text-neutral-100 mb-3">
                  2. How We Use Your Financial Information
                </h4>
                <p className="text-neutral-300 mb-3">
                  We use the information collected through bank account linking for the following purposes:
                </p>
                <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
                  <li><strong>ACH Transfers:</strong> To process deposits and withdrawals from your linked bank accounts</li>
                  <li><strong>Account Verification:</strong> To verify your identity and ensure compliance with regulatory requirements</li>
                  <li><strong>Fraud Prevention:</strong> To detect and prevent fraudulent transactions and unauthorized access</li>
                  <li><strong>Compliance:</strong> To comply with anti-money laundering (AML) and know-your-customer (KYC) regulations</li>
                  <li><strong>Service Improvement:</strong> To enhance our platform's security and user experience</li>
                </ul>
              </section>

              <section className="mb-6">
                <h4 className="text-lg font-semibold text-neutral-100 mb-3">
                  3. Information Sharing and Disclosure
                </h4>
                <p className="text-neutral-300 mb-3">
                  We take your privacy seriously and limit the sharing of your financial information:
                </p>
                <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
                  <li><strong>Service Providers:</strong> We share necessary information with Stripe for payment processing and financial services</li>
                  <li><strong>Legal Requirements:</strong> We may disclose information when required by law, court order, or regulatory authority</li>
                  <li><strong>Fraud Protection:</strong> Information may be shared with fraud prevention and financial crime prevention services</li>
                  <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred</li>
                </ul>
                <p className="text-neutral-300 mt-3">
                  <strong>We do not sell your financial information to third parties for marketing purposes.</strong>
                </p>
              </section>

              <section className="mb-6">
                <h4 className="text-lg font-semibold text-neutral-100 mb-3">
                  4. Data Security and Retention
                </h4>
                <p className="text-neutral-300 mb-3">
                  We implement industry-standard security measures to protect your financial information:
                </p>
                <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
                  <li><strong>Encryption:</strong> All financial data is encrypted in transit and at rest</li>
                  <li><strong>Access Controls:</strong> Strict access controls limit who can view your financial information</li>
                  <li><strong>Regular Audits:</strong> We regularly audit our systems for security vulnerabilities</li>
                  <li><strong>Data Retention:</strong> Financial information is retained only as long as necessary for the purposes outlined above</li>
                </ul>
              </section>

              <section className="mb-6">
                <h4 className="text-lg font-semibold text-neutral-100 mb-3">
                  5. Your Rights and Choices
                </h4>
                <p className="text-neutral-300 mb-3">
                  You have the following rights regarding your financial information:
                </p>
                <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
                  <li><strong>Access:</strong> You can request access to the financial information we have collected about you</li>
                  <li><strong>Correction:</strong> You can request correction of inaccurate financial information</li>
                  <li><strong>Deletion:</strong> You can request deletion of your financial information, subject to legal and regulatory requirements</li>
                  <li><strong>Opt-out:</strong> You can disconnect your bank accounts at any time through your account settings</li>
                  <li><strong>Data Portability:</strong> You can request a copy of your financial data in a portable format</li>
                </ul>
              </section>

              <section className="mb-6">
                <h4 className="text-lg font-semibold text-neutral-100 mb-3">
                  6. Third-Party Services
                </h4>
                <p className="text-neutral-300 mb-3">
                  We partner with Stripe to provide secure bank account linking and payment processing. Stripe's privacy practices are governed by their own privacy policy, which you can review at{' '}
                  <a
                    href="https://stripe.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-honey-500 hover:text-honey-400 underline"
                  >
                    stripe.com/privacy
                  </a>.
                </p>
                <p className="text-neutral-300">
                  Stripe Financial Connections provides a secure, tokenized connection to your bank accounts. Your actual bank credentials are never stored by the platform or shared with us.
                </p>
              </section>

              <section className="mb-6">
                <h4 className="text-lg font-semibold text-neutral-100 mb-3">
                  7. Changes to This Privacy Policy
                </h4>
                <p className="text-neutral-300">
                  We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of bank account linking services after such changes constitutes acceptance of the updated policy.
                </p>
              </section>

              <section className="mb-6">
                <h4 className="text-lg font-semibold text-neutral-100 mb-3">
                  8. Contact Us
                </h4>
                <p className="text-neutral-300">
                  If you have any questions about this privacy policy or our handling of your financial information, please contact us at:
                </p>
                <div className="text-neutral-300 mt-3">
                  <p><strong>Email:</strong> {TENANT.branding.supportEmail}</p>
                  <p><strong>Address:</strong> Privacy Team, [Company Address]</p>
                  <p><strong>Phone:</strong> [Privacy Contact Phone Number]</p>
                </div>
              </section>

              <div className="border-t border-neutral-700 pt-6 mt-8">
                <p className="text-neutral-300 text-sm">
                  <strong>By clicking "I Accept" below, you acknowledge that you have read, understood, and agree to this Privacy Policy for Bank Account Linking.</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-neutral-700 bg-neutral-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-neutral-400">
                <input
                  type="checkbox"
                  id="scroll-confirm"
                  checked={hasScrolledToBottom}
                  readOnly
                  className="rounded border-neutral-600 text-honey-500 focus:ring-honey-500"
                />
                <label htmlFor="scroll-confirm">
                  I have read the entire privacy policy
                </label>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-neutral-400 hover:text-neutral-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onAccept}
                  disabled={!hasScrolledToBottom}
                  className="px-6 py-2 bg-honey-500 text-neutral-900 font-semibold rounded-lg hover:bg-honey-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  I Accept
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PrivacyPolicyModal;